import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { MeQuery } from '@/graphql/graphql';
import { useImageUpload } from '@/hooks/useImageUpload';
import { updateUserMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { CameraSolid, Envelope } from '@mynaui/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Separator } from '../ui/separator';
import { ChangeEmailDialog } from './dialogs/ChangeEmailDialog';

interface ProfileInformationProps {
  user: MeQuery['me'];
}

export const ProfileInformation = ({ user }: ProfileInformationProps) => {
  const { openDialog } = useDialog();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { uploadImage, isUploading, uploadProgress } = useImageUpload({
    type: 'USER_AVATAR',
  });

  const _handleCancel = () => {
    // This will be overridden inside Formik render via formik.resetForm
    setIsEditing(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const _handleChange = () => {
    setIsEditing(true);
  };

  const _handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setIsEditing(true);
    }
  };

  // Cleanup object URL on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
        <CardDescription>Update your personal information and profile picture.</CardDescription>
      </CardHeader>
      <CardContent className='gap-6'>
        <div className='flex items-center gap-6'>
          <div className='relative'>
            <img
              src={previewUrl || user.avatar?.url || getPlaceholderAvatar(user.email || user.id || '')}
              alt=''
              className='size-20 object-cover rounded-full border border-slate-200/70 dark:border-stone-800/70'
            />
            <label
              htmlFor='avatar-upload'
              className={`absolute -bottom-1 -right-1 bg-slate-900 hover:bg-slate-950 text-white dark:bg-white dark:text-stone-900 rounded-full p-2 transition-colors ${
                isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              }`}>
              <CameraSolid className='size-4 stroke-2' />
              <input
                id='avatar-upload'
                type='file'
                accept='image/*'
                className='hidden'
                onChange={_handleAvatarUpload}
                disabled={isUploading}
              />
            </label>
            {isUploading && (
              <div className='absolute inset-0 flex items-center justify-center bg-black/50 rounded-full'>
                <span className='text-white text-xs font-medium'>{uploadProgress}%</span>
              </div>
            )}
          </div>
          <div className='flex flex-col gap-1.5'>
            <h3 className='text-lg font-medium leading-none text-slate-900 dark:text-white'>{user.name}</h3>
            <p className='text-slate-500 dark:text-stone-400 text-sm font-medium leading-none'>{user.email}</p>
            {selectedFile && (
              <p className='text-xs text-slate-600 dark:text-stone-400'>New image selected: {selectedFile.name}</p>
            )}
          </div>
        </div>
        <Separator />
        <Formik
          initialValues={{
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            mobileNumber: user.mobileNumber || '',
          }}
          enableReinitialize
          onSubmit={async (values, { resetForm }) => {
            const t = toast.loading('Saving user data...');
            try {
              let avatarId: string | undefined = undefined;

              if (selectedFile) {
                try {
                  avatarId = await uploadImage(selectedFile);
                } catch (error) {
                  toast.error('Failed to upload avatar', { id: t });
                  return;
                }
              }

              await execute(updateUserMutation, {
                id: user.id,
                input: {
                  ...values,
                  ...(avatarId && { avatarId }),
                },
              });

              await queryClient.invalidateQueries({ queryKey: ['me'] });
              toast.success('User data saved', { id: t });
              setSelectedFile(null);
              setPreviewUrl(null);
              setIsEditing(false);
              resetForm({ values });
            } catch (error) {
              toast.error('Failed to save user data', { id: t });
            }
          }}>
          {(formik) => (
            <Form>
              {/* Profile Form */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='space-y-2'>
                  <Label htmlFor='firstName'>First Name</Label>
                  <Field name='firstName'>
                    {({ field }: FieldProps) => <Input id='firstName' {...field} disabled={!isEditing} />}
                  </Field>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='lastName'>Last Name</Label>
                  <Field name='lastName'>
                    {({ field }: FieldProps) => <Input id='lastName' {...field} disabled={!isEditing} />}
                  </Field>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='mobileNumber'>Mobile Number</Label>
                  <Field name='mobileNumber'>
                    {({ field }: FieldProps) => (
                      <Input id='mobileNumber' placeholder='Enter mobile number' {...field} disabled={!isEditing} />
                    )}
                  </Field>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='email'>Email Address</Label>
                  <div className='flex items-center space-x-2'>
                    <Input id='email' value={user.email} disabled className='flex-1' />
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => openDialog(<ChangeEmailDialog userId={user.id} />)}>
                      <Envelope className='h-4 w-4 mr-2 stroke-2' />
                      Change
                    </Button>
                  </div>
                </div>
              </div>
              <div className='flex justify-end space-x-2 mt-4'>
                {!isEditing ? (
                  <Button type='button' onClick={_handleChange}>
                    Change
                  </Button>
                ) : (
                  <>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => {
                        formik.resetForm();
                        _handleCancel();
                      }}>
                      Cancel
                    </Button>
                    <Button type='submit'>Save Changes</Button>
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};
